import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Calculator, FileText, AlertTriangle, CheckCircle } from 'lucide-react';

const PRODUCT_TYPES = [
  { value: 'roll', label: 'Roll' },
  { value: 'pouch', label: 'Pouch' },
  { value: 'sleeve', label: 'Sleeve' },
  { value: 'hair_bag', label: 'Hair Bag' },
];

const MaterialRequirements = () => {
  const [routings, setRoutings] = useState([]);
  const [finalProductType, setFinalProductType] = useState('pouch');
  const [quantity, setQuantity] = useState(1000);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingIndent, setCreatingIndent] = useState(false);

  useEffect(() => {
    api.get('/packaging/product-routings').then(r => setRoutings(r.data)).catch(() => {});
  }, []);

  const compute = async (createIndent = false) => {
    if (!quantity || quantity <= 0) { alert('Enter a quantity greater than 0'); return; }
    createIndent ? setCreatingIndent(true) : setLoading(true);
    try {
      const res = await api.post('/packaging/material-requirements', {
        final_product_type: finalProductType,
        quantity: Number(quantity),
        create_indent: createIndent,
      });
      setResult(res.data);
      if (createIndent && res.data.indent_created) {
        alert(`Material Indent ${res.data.indent_created.indent_number} created for the shortfall items.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to compute requirements');
    } finally { setLoading(false); setCreatingIndent(false); }
  };

  const routeForType = routings.find(r => r.final_product_type === finalProductType);

  return (
    <div data-testid="material-requirements-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Material Requirements (MRP)</h1>
      </div>

      <div className="card mb-4">
        <div className="flex gap-4" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
            <label className="form-label">Final Product</label>
            <select className="form-select" value={finalProductType} onChange={(e) => { setFinalProductType(e.target.value); setResult(null); }} data-testid="product-type-select">
              {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '160px' }}>
            <label className="form-label">Quantity to Produce</label>
            <input type="number" className="form-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="quantity-input" />
          </div>
          <button onClick={() => compute(false)} className="btn btn-primary" disabled={loading} data-testid="compute-btn">
            <Calculator size={16} /> {loading ? 'Computing...' : 'Compute Requirements'}
          </button>
        </div>
        {routeForType && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            Route: {(routeForType.process_sequence || []).map(s => s.replace(/_/g, ' ')).join('  →  ')}
          </div>
        )}
      </div>

      {result && (
        <>
          <div className="card mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                Raw Material Requirements — {Number(result.quantity).toLocaleString()} × {finalProductType.replace(/_/g, ' ')}
              </h2>
              <div className="flex items-center gap-3">
                {result.has_shortfall ? (
                  <span className="badge badge-warning"><AlertTriangle size={12} /> Shortfall</span>
                ) : (
                  <span className="badge badge-success"><CheckCircle size={12} /> Stock OK</span>
                )}
                {result.has_shortfall && (
                  <button onClick={() => compute(true)} className="btn btn-secondary btn-sm" disabled={creatingIndent} data-testid="create-indent-btn">
                    <FileText size={14} /> {creatingIndent ? 'Creating...' : 'Generate Material Indent'}
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead><tr><th>Material</th><th>SKU</th><th className="text-right">Required</th><th className="text-right">In Stock</th><th className="text-right">Shortfall</th><th>Status</th></tr></thead>
                <tbody>
                  {result.requirements.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No raw materials mapped for this route.</td></tr> : result.requirements.map((ln, i) => (
                    <tr key={i} data-testid={`req-row-${ln.sku || i}`}>
                      <td>{ln.name}</td>
                      <td className="font-mono text-sm">{ln.sku || '-'}</td>
                      <td className="text-right tabular-nums">{ln.required.toLocaleString()} {ln.unit}</td>
                      <td className="text-right tabular-nums">{ln.available.toLocaleString()} {ln.unit}</td>
                      <td className="text-right tabular-nums" style={{ color: ln.shortfall > 0 ? '#DC2626' : 'inherit' }}>{ln.shortfall > 0 ? `${ln.shortfall.toLocaleString()} ${ln.unit}` : '-'}</td>
                      <td>{ln.in_stock ? <span className="badge badge-success">In Stock</span> : <span className="badge badge-warning">Short</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 12px' }}>Process Breakdown</h2>
            <div className="table-container">
              <table>
                <thead><tr><th>Process</th><th>Inputs (required for this qty)</th></tr></thead>
                <tbody>
                  {result.process_breakdown.map((p, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{p.process}</td>
                      <td className="text-sm">
                        {p.inputs.map((inp, j) => (
                          <span key={j} style={{ marginRight: '12px' }}>
                            {inp.name}: {inp.required.toLocaleString()} {inp.unit}
                            <span className="text-xs text-muted"> [{inp.input_type === 'rm' ? 'RM' : 'WIP'}]</span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MaterialRequirements;
